import { useState, useMemo, useContext, useCallback } from 'react';
import { ModalPortal, SystemFeaturesContext, UserContext } from '@/components';
import { CognitiveSearchResultModel, ImageLibrarySearchResult, ScrapbookContentType, ScrapbookSaveItem } from '@/types';
import ImageLibraryResultGrid from './ImageLibraryResultGrid';
import DownloadSimpleLight from '@/assets/downloadSimpleLight.svg';
import SelectionSlashLight from '@/assets/selectionSlashLight.svg';
import ScissorsLight from '@/assets/scissors-light.svg';
import { useTranslations } from 'next-intl';
import { downloadImages } from '@/api';
import { SaveToScrapbookButton } from '@/components/scrapbook';

interface ImageLibraryResultType {
  result: CognitiveSearchResultModel<ImageLibrarySearchResult>;
  loading?: boolean;
  onLoadMore: () => void;
}

const ImageLibraryResult = (data: ImageLibraryResultType) => {
  const { result, onLoadMore, loading } = data;
  const user = useContext(UserContext);
  const t = useTranslations();
  const [selectedImages, setSelectedImages] = useState<ImageLibrarySearchResult[]>([]);
  const { saveToScrapbook: saveToScrapbookFeature } = useContext(SystemFeaturesContext);
  const selectedIds = useMemo(() => selectedImages.map((i) => i.media.mediaId), [selectedImages]);
  const showDownloadBtn = useMemo(() => user?.isAuthenticated || user?.isIpBasedIdentity, [user]);
  const showSaveToScrapbook = useMemo(() => user?.isAuthenticated, [user]);

  const onSelectImage = (imageLibrary: ImageLibrarySearchResult) => {
    setSelectedImages((prev) => {
      const isSelected = selectedIds.includes(imageLibrary.media.mediaId);
      if (isSelected) {
        return prev.filter((i) => i.media.mediaId !== imageLibrary.media.mediaId);
      } else {
        return [...prev, imageLibrary];
      }
    });
  };

  const downloadAllSelectedImages = useCallback(() => {
    downloadImages(selectedIds);
  }, [selectedIds]);

  const scrapbookContentGenerator = useCallback((): ScrapbookSaveItem[] => {
    return selectedImages.map((imageLibrary) => {
      const content = {
        imageId: imageLibrary.media.mediaId,
        imageUrl: imageLibrary.media.imageUrl,
        imageCaption: imageLibrary.media.caption,
        imageCopyright: imageLibrary.media.copyrightText,
        pageId: imageLibrary.report?.reportId,
        pageUrl: imageLibrary.report?.reportUrl,
        pageTitle: imageLibrary.report?.title,
      };
      return {
        path: `img.${imageLibrary.media.mediaId}.json`,
        contentType: 'application/x.stylus.lnk.img+json',
        content: JSON.stringify(content),
      };
    });
  }, [selectedImages]);

  return (
    <>
      <span className="px-3 pb-1 text-sm md:px-0">{t('imageLibrary.$ximages', { x: result.totalResults })}</span>
      <ImageLibraryResultGrid
        result={result}
        showDownloadBtn={showDownloadBtn}
        onSelectImage={onSelectImage}
        selectedIds={selectedIds}
        showSaveToScrapbook={showSaveToScrapbook}
        onLoadMore={onLoadMore}
        loading={loading}
      />
      <ModalPortal>
        <div className="scrollbar-placeholder fixed bottom-0 z-50 w-full bg-gray-100">
          <div className="mx-auto flex w-full max-w-[1800px] items-center gap-2 pt-3 pb-3 md:px-3 xl:px-6">
            <button
              className="btn border-gray-4 [&>svg]:!fill-gray-4 !items-start border bg-white !py-1 !pr-1 !pl-3 !font-medium whitespace-nowrap hover:bg-gray-100 disabled:!text-gray-400 disabled:hover:bg-white disabled:[&>svg]:!fill-gray-400"
              onClick={() => setSelectedImages([])}
              disabled={selectedImages.length === 0}
            >
              <SelectionSlashLight className="mt-0.5 h-5 w-5" />
              <span className="mt-px hidden text-sm md:block">{t('general.$unselect')}</span>
              <span className="bg-gray-5 flex h-full min-h-6 min-w-6 items-center justify-center rounded-full px-1.5 pt-px pb-0.5 text-sm">
                {selectedImages.length > 0 ? selectedImages.length : 0}
              </span>
            </button>

            {showDownloadBtn && (
              <button
                className="btn border-gray-4 [&>svg]:!fill-gray-4 !items-start border bg-white !py-1 !pr-1 !pl-3 !font-medium whitespace-nowrap hover:bg-gray-100 disabled:!text-gray-400 disabled:hover:bg-white disabled:[&>svg]:!fill-gray-400"
                onClick={downloadAllSelectedImages}
                disabled={selectedImages.length === 0}
              >
                <DownloadSimpleLight className="mt-0.5 h-5 w-5" />
                <span className="mt-px hidden text-sm md:block">{t('general.$download')}</span>
                <span className="bg-gray-5 flex h-full min-h-6 min-w-6 items-center justify-center rounded-full px-1.5 pt-px pb-0.5 text-sm">
                  {selectedImages.length > 0 ? selectedImages.length : 0}
                </span>
              </button>
            )}
            {showSaveToScrapbook && (
              <SaveToScrapbookButton
                scrapbookContent={{}}
                scrapbookContentType={ScrapbookContentType.Image}
                scrapbookPath=""
                successMessage="Report saved to scrapbook successfully"
                tooltipProps={{ inactive: saveToScrapbookFeature, placement: 'top', content: '' }}
                bulkContentGenerator={scrapbookContentGenerator}
                className="btn border-gray-4 [&>svg]:!fill-gray-4 !items-start border bg-white !py-1 !pr-1 !pl-3 !font-medium whitespace-nowrap hover:bg-gray-100 disabled:!text-gray-400 disabled:hover:bg-white disabled:[&>svg]:!fill-gray-400"
                disabled={selectedImages.length === 0}
              >
                <ScissorsLight className="mt-0.5 h-5 w-5" />
                <span className="mt-px hidden text-sm md:block">{'Save to Scrapbook'}</span>
                <span className="bg-gray-5 flex h-full min-h-6 min-w-6 items-center justify-center rounded-full px-1.5 pt-px pb-0.5 text-sm">
                  {selectedImages.length > 0 ? selectedImages.length : 0}
                </span>
              </SaveToScrapbookButton>
            )}
          </div>
        </div>
      </ModalPortal>
    </>
  );
};

export default ImageLibraryResult;
